import { createSelector } from '@reduxjs/toolkit';
import { DEFAULT_ADDITIONAL_SALARY_TYPES } from 'LixaniAPI/enums';
import { selectSelectedProject } from 'store/projects/selectors/projectsSelectors';

const selectEmployerAllowancesState = (state) => state.employerAllowances;

export const selectEmployerAllowanceDrafts = createSelector(
  [selectEmployerAllowancesState],
  (state) => state.employerAllowanceDrafts.entities
);
export const selectEmployerAllowanceDraftsIds = createSelector(
  [selectEmployerAllowancesState],
  (state) => state.employerAllowanceDrafts.ids
);
export const selectEmployerAllowanceDraftsArray = createSelector(
  [selectEmployerAllowanceDrafts],
  (drafts) => Object.values(drafts)
);

export const selectIdsOfEmployerAllowanceDraftsMissingRequiredAttributes =
  createSelector([selectEmployerAllowanceDraftsArray], (drafts) => {
    const absenceTypeAllowances = Object.values(DEFAULT_ADDITIONAL_SALARY_TYPES)
      .filter((salaryType) => salaryType.baseType === 'ABSENCE')
      .map((salaryType) => salaryType.salaryType);

    const idsOfDraftsMissingAttributes = drafts
      .filter(
        (draft) => {
          const isAbsenceTypeAllowance = absenceTypeAllowances.includes(
            draft.salaryType
          );

          const isValid = !isAbsenceTypeAllowance
            ? (!draft.salaryType || !draft.unit) && !draft.deletedAt
            : !draft.salaryType && !draft.deletedAt;

          return isValid;
        }
        // required attributes
      )
      .map((draft) => draft.id);

    return idsOfDraftsMissingAttributes;
  });
export const selectIsEmployerAllowanceDraftsMissingRequiredAttributes =
  createSelector(
    [selectIdsOfEmployerAllowanceDraftsMissingRequiredAttributes],
    (ids) => {
      return ids.length > 0;
    }
  );

export const selectIsEmployerAllowanceDrafts = createSelector(
  [selectEmployerAllowanceDraftsArray],
  (drafts) => drafts.length > 0
);

export const selectEmployerAllowances = createSelector(
  [selectEmployerAllowancesState],
  (state) => state.employerAllowances.entities
);
export const selectEmployerAllowancesIds = createSelector(
  [selectEmployerAllowancesState],
  (state) => state.employerAllowances.ids
);

export const selectEmployerAllowancesArray = createSelector(
  [selectEmployerAllowances],
  (employerAllowances) => Object.values(employerAllowances)
);
export const selectEmployerAllowancesBySelectedProjectBusinessId =
  createSelector(
    [selectEmployerAllowances, selectSelectedProject],
    (employerAllowances, selectedProject) => {
      let employerAllowancesToFilter = { ...employerAllowances };
      for (const key in employerAllowancesToFilter) {
        if (
          employerAllowancesToFilter[key].businessId !==
          selectedProject.business_id
        ) {
          delete employerAllowancesToFilter[key];
        }
      }

      return employerAllowancesToFilter;
    }
  );

export const selectEmployerAllowancesWithMergedDrafts = createSelector(
  [
    selectEmployerAllowancesBySelectedProjectBusinessId,
    selectEmployerAllowanceDrafts,
  ],
  (allowances, drafts) => {
    return { ...allowances, ...drafts };
  }
);

export const selectEmployerAllowancesWithDraftsArray = createSelector(
  [selectEmployerAllowancesWithMergedDrafts],
  (allowancesWithMergedDrafts) => {
    return Object.values(allowancesWithMergedDrafts).filter(
      (employerAllowance) => !employerAllowance.deletedAt
    );
  }
);

export const selectIsEmployerAllowancesSavePending = createSelector(
  [selectEmployerAllowancesState],
  (state) => state.savePending
);
export const selectIsEmployerAllowancesFetchPending = createSelector(
  [selectEmployerAllowancesState],
  (state) => state.loading
);

export const selectEmployerAllowancesByBusinessId = createSelector(
  [selectEmployerAllowancesArray, (_, args) => args],
  (employerAllowances, businessId) => {
    const businessAllowances = employerAllowances
      ? employerAllowances.filter(
          (allowance) => allowance.businessId === businessId
        )
      : [];

    return businessAllowances;
  }
);
